import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import dayjs from "../shared utils/dayjsConfig.js";
import useCampuses from "../Timetable/hooks/useCampuses.js";
import useDailyRota from "./hooks/useDailyRota.js";
import useRotaTemplates from "./hooks/useRotaTemplates.js";
import {
  blocksFromTemplate,
  assignBlockLanes,
  computeStaffHours,
  staffKeyOf,
  excusedKeyOf,
} from "./utils/rotaModel.js";

const fmtDur = (m) => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
};

// ---------------------------------------------------------------------------
// Daily Rota — a vertical, time-positioned timeline of the day's blocks. Each
// block is a day element listing the staff on it. Drag the title bar to move,
// drag the top/bottom edge to resize (15-min snap), double-click a title/role
// to rename, drag a name from the on-duty rail into a block to assign.
//
// Data is live: blocks seed from the day template + that date's activity groups
// (or a saved rota); the rail + key-staff come from the weekly staff-scheduling
// feed. Persistence is via useDailyRota.save (backend: docs/backend-ticket-
// daily-rota.md) — until that endpoint ships, Save reports it's pending.
// ---------------------------------------------------------------------------

const DAY_START = 7 * 60;
const DAY_END = 23 * 60;
const PX = 1.12;
const SNAP = 15;
const MIN_DUR = 15;

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const snap = (m) => Math.round(m / SNAP) * SNAP;
const yOf = (m) => (m - DAY_START) * PX;
const fmt = (m) => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h < 10 ? "0" : ""}${h}:${mm < 10 ? "0" : ""}${mm}`;
};

let _uid = 0;
const uid = () => `u${++_uid}`;

function kindColors(kind, dark) {
  const light = {
    meal: ["#fbf1e0", "#dd9a3c", "#7f5410"],
    act: ["#eee9fb", "#7c5cd6", "#472c9c"],
    class: ["#e7f0fb", "#3f7fce", "#215394"],
    night: ["#e6e8f5", "#3a4491", "#232c69"],
    custom: ["#e2f3ef", "#2f9e8b", "#186253"],
  };
  const night = {
    meal: ["#372c19", "#c68f30", "#f1c884"],
    act: ["#29244e", "#9784e0", "#c9bef6"],
    class: ["#1b2a3e", "#5b8fd0", "#abc9ef"],
    night: ["#1f253d", "#616ab6", "#b6bde9"],
    custom: ["#152e28", "#3fae99", "#92dacb"],
  };
  const [bg, bd, ink] = (dark ? night : light)[kind] || (dark ? night : light).custom;
  return { bg, bd, ink };
}

function Editable({ value, onCommit, sx }) {
  const ref = useRef(null);
  return (
    <Box
      ref={ref}
      component="span"
      suppressContentEditableWarning
      onDoubleClick={() => {
        ref.current.contentEditable = "true";
        ref.current.focus();
        const sel = window.getSelection();
        if (sel && ref.current) sel.selectAllChildren(ref.current);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          ref.current.blur();
        }
      }}
      onBlur={() => {
        ref.current.contentEditable = "false";
        onCommit(ref.current.textContent.trim());
      }}
      onPointerDown={(e) => {
        if (ref.current.contentEditable === "true") e.stopPropagation();
      }}
      sx={{ outline: "none", cursor: "text", ...sx }}
    >
      {value}
    </Box>
  );
}

export default function DailyRotaView({
  selectedDate,
  setSelectedDate,
  selectedCampusId,
  setSelectedCampusId,
}) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const { campuses = [], loading: campusesLoading } = useCampuses();

  const campusName =
    campuses.find((c) => String(c.id) === String(selectedCampusId))?.name || null;
  const dateStr = dayjs(selectedDate || undefined).format("YYYY-MM-DD");

  const { loading, error, seedBlocks, staffPool, keyStaff, staffIndex, hoursBaseline, hoursBaselineAt, save } =
    useDailyRota(campusName, dateStr);
  const { templates, saveTemplate, removeTemplate } = useRotaTemplates(campusName);

  const [blocks, setBlocks] = useState([]);
  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null);
  const [ghost, setGhost] = useState(null);
  const [dropId, setDropId] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const dragRef = useRef(null);
  const staffDragRef = useRef(null);

  // Reload the editable blocks whenever the seed (campus/date/saved) changes.
  useEffect(() => {
    setBlocks(seedBlocks);
    setDirty(false);
  }, [seedBlocks]);

  const touch = () => setDirty(true);

  // ---- block move / resize ------------------------------------------------
  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const dMin = (e.clientY - d.y0) / PX;
    const dur = d.e0 - d.s0;
    let start = d.s0;
    let end = d.e0;
    if (d.mode === "move") {
      start = clamp(snap(d.s0 + dMin), DAY_START, DAY_END - dur);
      end = start + dur;
    } else if (d.mode === "top") {
      start = clamp(snap(d.s0 + dMin), DAY_START, d.e0 - MIN_DUR);
      end = d.e0;
    } else {
      end = clamp(snap(d.e0 + dMin), d.s0 + MIN_DUR, DAY_END);
      start = d.s0;
    }
    setBlocks((prev) => prev.map((b) => (b.id === d.id ? { ...b, start, end } : b)));
  }, []);

  const onPointerUp = useCallback(() => {
    if (dragRef.current) touch();
    dragRef.current = null;
    setActiveId(null);
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  }, [onPointerMove]);

  const startDrag = useCallback(
    (e, block, mode) => {
      e.stopPropagation();
      dragRef.current = { mode, id: block.id, y0: e.clientY, s0: block.start, e0: block.end };
      setActiveId(block.id);
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    },
    [onPointerMove, onPointerUp]
  );

  // ---- staff drag from rail ----------------------------------------------
  const onStaffMove = useCallback((e) => {
    if (!staffDragRef.current) return;
    setGhost({ name: staffDragRef.current.name, x: e.clientX, y: e.clientY });
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const blk = el && el.closest("[data-block-id]");
    // Reflected blocks (activities/classes) aren't drop targets — their staff is
    // owned by the Activities / Classes modules.
    setDropId(blk && blk.getAttribute("data-reflected") !== "1" ? blk.getAttribute("data-block-id") : null);
  }, []);

  const onStaffUp = useCallback(
    (e) => {
      const s = staffDragRef.current;
      window.removeEventListener("pointermove", onStaffMove);
      window.removeEventListener("pointerup", onStaffUp);
      staffDragRef.current = null;
      setGhost(null);
      setDropId(null);
      if (!s) return;
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const blk = el && el.closest("[data-block-id]");
      if (!blk || blk.getAttribute("data-reflected") === "1") return; // can't staff a reflected block
      const id = blk.getAttribute("data-block-id");
      setBlocks((prev) =>
        prev.map((b) => {
          if (b.id !== id) return b;
          if (b.people.some((p) => p.name === s.name)) return b;
          const next = { ...b, people: [...b.people, { name: s.name, role: "", staffId: s.staffId }] };
          // Putting someone back on residential un-excuses them: no claw-back
          // while they're rostered on it.
          if (b.residential) {
            const key = staffKeyOf(s, staffIndex);
            next.excused = (b.excused || []).filter((entry) => excusedKeyOf(entry, staffIndex) !== key);
          }
          return next;
        })
      );
      touch();
    },
    [onStaffMove, staffIndex]
  );

  const startStaffDrag = useCallback(
    (e, staff) => {
      if (staff.off) return;
      e.preventDefault();
      staffDragRef.current = { name: staff.name, staffId: staff.staffId };
      setGhost({ name: staff.name, x: e.clientX, y: e.clientY });
      window.addEventListener("pointermove", onStaffMove);
      window.addEventListener("pointerup", onStaffUp);
    },
    [onStaffMove, onStaffUp]
  );

  // ---- block mutations ----------------------------------------------------
  const removeBlock = (id) => { setBlocks((p) => p.filter((b) => b.id !== id)); touch(); };
  const removePerson = (id, i) => {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== id) return b;
        const person = b.people[i];
        const next = { ...b, people: b.people.filter((_, k) => k !== i) };
        // Excusing from residential records it so their overtime can be clawed back.
        if (b.residential && person) {
          next.excused = [...new Set([...(b.excused || []), staffKeyOf(person, staffIndex)])].filter(Boolean);
        }
        return next;
      })
    );
    touch();
  };
  const commitTitle = (id, v) => { setBlocks((p) => p.map((b) => (b.id === id ? { ...b, title: v || "Untitled" } : b))); touch(); };
  const commitRole = (id, i, v) => { setBlocks((p) => p.map((b) => (b.id === id ? { ...b, people: b.people.map((q, k) => (k === i ? { ...q, role: v } : q)) } : b))); touch(); };
  const addBlock = () => { setBlocks((p) => [...p, { id: uid(), kind: "custom", title: "New block", start: 660, end: 720, people: [] }]); touch(); };

  const onSave = async () => {
    setSaving(true);
    try {
      await save(blocks);
      setDirty(false);
      setSnack({ sev: "success", msg: "Rota saved" });
    } catch (e) {
      const pending = e?.status === 404 || e?.status === 405 || e?.status === 501;
      setSnack({
        sev: pending ? "info" : "error",
        msg: pending
          ? "Saving isn't available yet — the rota backend is pending."
          : e?.message || "Save failed",
      });
    } finally {
      setSaving(false);
    }
  };

  const withFreshIds = (blocks) => blocks.map((b) => ({ ...b, id: uid() }));

  // Add a template's blocks ON TOP of the current rota (keeps everything, fresh
  // ids so they don't collide). Used for overlay templates and the ＋ button.
  const addTemplate = (tpl) => {
    setBlocks((prev) => [...prev, ...withFreshIds(blocksFromTemplate(tpl))]);
    setDirty(true);
    setSnack({ sev: "success", msg: `Added "${tpl.name}"` });
  };

  // Clicking a template: overlay templates ADD; the rest REPLACE the rota-owned
  // blocks (keeping the live reflected activity/class blocks).
  const applyTemplate = (tpl) => {
    if (tpl.overlay) {
      addTemplate(tpl);
      return;
    }
    // Keep the live reflected activity/class blocks AND the auto-filled
    // residential block; replace only the other rota-owned blocks.
    setBlocks((prev) => [...prev.filter((b) => b.reflected || b.residential), ...withFreshIds(blocksFromTemplate(tpl))]);
    setDirty(true);
    setSnack({ sev: "success", msg: `Applied "${tpl.name}"` });
  };

  const confirmSaveTemplate = async () => {
    const name = tplName.trim();
    if (!name) return;
    try {
      await saveTemplate(name, blocks);
      setSnack({ sev: "success", msg: `Saved template "${name}"` });
    } catch (e) {
      const pending = e?.status === 404 || e?.status === 405 || e?.status === 501;
      setSnack({
        sev: pending ? "info" : "error",
        msg: pending
          ? "Template saving isn't available yet — the rota backend is pending."
          : e?.message || "Couldn't save template",
      });
    } finally {
      setTplDialogOpen(false);
      setTplName("");
    }
  };

  const deleteTemplate = async (tpl) => {
    try {
      await removeTemplate(tpl.id);
    } catch (e) {
      setSnack({ sev: "error", msg: e?.message || "Couldn't delete template" });
    }
  };

  const hourLines = useMemo(() => {
    const lines = [];
    for (let m = DAY_START; m <= DAY_END; m += 30) lines.push(m);
    return lines;
  }, []);

  // Side-by-side columns for overlapping blocks, recomputed as blocks move.
  const layout = useMemo(() => assignBlockLanes(blocks), [blocks]);

  // Per-person effective hours. The backend supplies the base figure
  // (`scheduledMinutes`); edits here move it by their own delta. Meals +
  // residential presence don't count, classes count 3h, concurrent blocks count
  // once, and excusing an over-hours person from residential claws back their
  // overtime (front-end only).
  const staffHours = useMemo(
    () => computeStaffHours(blocks, { baseline: hoursBaseline, baselineAt: hoursBaselineAt, index: staffIndex }),
    [blocks, hoursBaseline, hoursBaselineAt, staffIndex]
  );
  // Keyed by staffId where resolvable, so `person` (not just their name) is what
  // you look up with.
  const hFor = (person) =>
    staffHours[staffKeyOf(person, staffIndex)] ||
    { baseline: 0, delta: 0, raw: 0, clawback: 0, effective: 0, over: false };
  const isOver = (person) => hFor(person).over;
  const hoursTitle = (person) => {
    const h = hFor(person);
    const parts = [];
    if (hoursBaseline) {
      parts.push(`${fmtDur(h.baseline)} scheduled`);
      if (h.delta) parts.push(`${h.delta > 0 ? "+" : "−"} ${fmtDur(Math.abs(h.delta))} unsaved changes`);
    } else {
      parts.push(`${fmtDur(h.raw)} assigned`);
    }
    if (h.clawback) parts.push(`− ${fmtDur(h.clawback)} residential claw-back`);
    if (parts.length === 1 && !h.over) return undefined;
    return `${parts.join(" ")} = ${fmtDur(h.effective)}${h.over ? " — over 7h" : ""}`;
  };

  const border = dark ? "#2c3246" : "#dde2ec";
  const surface = dark ? "#1a1e2b" : "#ffffff";
  const surface2 = dark ? "#232838" : "#f4f6fa";
  const muted = dark ? "#99a0b7" : "#676e85";

  const keyRows = [
    ["Camp Manager", keyStaff.campManager],
    ["DSL", keyStaff.dsl],
    ["First Aid", keyStaff.firstAid],
  ].filter(([, names]) => names && names.length);

  const campusValue = campuses.some((c) => String(c.id) === String(selectedCampusId))
    ? String(selectedCampusId)
    : "";
  const stepDay = (delta) =>
    setSelectedDate && setSelectedDate(dayjs(selectedDate || undefined).add(delta, "day"));

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1200, mx: "auto" }}>
      {/* header */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2, pb: 1.5, borderBottom: `1px solid ${border}`, mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "primary.main", fontWeight: 700 }}>
            Daily rota
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{campusName || "Daily Rota"}</Typography>
        </Box>

        {/* campus + date controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 190 }} disabled={campusesLoading}>
            <InputLabel id="rota-campus-label">Campus</InputLabel>
            <Select
              labelId="rota-campus-label"
              label="Campus"
              value={campusValue}
              onChange={(e) => setSelectedCampusId && setSelectedCampusId(e.target.value)}
            >
              <MenuItem value=""><em>Select a campus</em></MenuItem>
              {campuses.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton size="small" onClick={() => stepDay(-1)} aria-label="Previous day"><NavigateBefore /></IconButton>
            <DatePicker
              value={dayjs(selectedDate || undefined)}
              onChange={(d) => d && setSelectedDate && setSelectedDate(dayjs(d))}
              format="ddd D MMM YYYY"
              slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
            />
            <IconButton size="small" onClick={() => stepDay(1)} aria-label="Next day"><NavigateNext /></IconButton>
          </Box>
        </Box>

        <Box sx={{ flex: 1 }} />
        {keyRows.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {keyRows.map(([role, names]) => (
              <Box key={role} sx={{ border: `1px solid ${border}`, borderRadius: 999, px: 1.4, py: 0.5, fontSize: 12.5, bgcolor: surface }}>
                <Box component="span" sx={{ color: muted, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", mr: 0.7 }}>{role}</Box>
                {names.join(", ")}
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {dirty && <Typography sx={{ fontSize: 12, color: muted }}>Unsaved</Typography>}
          <Button size="small" onClick={addBlock} disabled={!campusName}>+ Add block</Button>
          <Button size="small" onClick={() => { setBlocks(seedBlocks); setDirty(false); }} disabled={!campusName}>Reset</Button>
          <Button size="small" variant="contained" onClick={onSave} disabled={!campusName || saving || loading}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Box>

      {!campusName && (
        <Box sx={{ p: 5, textAlign: "center", color: muted }}>
          <Typography>Select a campus above to plan its daily rota.</Typography>
        </Box>
      )}

      {campusName && (
      <>
      {/* legend */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5, fontSize: 12, color: muted }}>
        {[["class", "Classes / template"], ["act", "Activities"], ["meal", "Meals"], ["night", "Night"], ["custom", "Custom"]].map(([k, lbl]) => {
          const c = kindColors(k, dark);
          return (
            <Box key={k} component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.7 }}>
              <Box sx={{ width: 11, height: 11, borderRadius: 0.6, bgcolor: c.bg, border: `1px solid ${c.bd}` }} />
              {lbl}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "188px 1fr 210px" }, gap: 2, alignItems: "start" }}>
        {/* templates */}
        <Box sx={{ position: { md: "sticky" }, top: 12, bgcolor: surface, border: `1px solid ${border}`, borderRadius: 2, p: 1.5 }}>
          <Typography sx={{ fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase", color: muted, mb: 0.3 }}>Templates</Typography>
          <Typography sx={{ fontSize: 11.5, color: muted, mb: 1.5 }}>Click to apply (replaces meals/night). ＋ or an add-on layers a template on top.</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.9 }}>
            {templates.map((tpl) => (
              <Box
                key={tpl.id}
                onClick={() => applyTemplate(tpl)}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.5, bgcolor: surface2,
                  border: `1px solid ${border}`, borderRadius: 1, px: 1.1, py: 0.8, cursor: "pointer",
                  "&:hover": { borderColor: theme.palette.primary.main },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.25, minWidth: 0, flex: 1 }}>
                  <Box component="span" sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tpl.name}</Box>
                  <Box component="span" sx={{ fontSize: 10.5, color: muted }}>
                    {(tpl.blocks || []).length} blocks{tpl.overlay ? " · add-on" : tpl.builtin ? " · starter" : ""}
                  </Box>
                </Box>
                {!tpl.overlay && (
                  <Box component="button" aria-label={`Add ${tpl.name} on top`} title="Add on top"
                    onClick={(e) => { e.stopPropagation(); addTemplate(tpl); }}
                    sx={{ border: "none", background: "transparent", color: muted, opacity: 0.6, fontSize: 17, lineHeight: 1, cursor: "pointer", px: 0.3, "&:hover": { opacity: 1, color: theme.palette.primary.main } }}>
                    ＋
                  </Box>
                )}
                {!tpl.builtin && (
                  <Box component="button" aria-label="Delete template"
                    onClick={(e) => { e.stopPropagation(); deleteTemplate(tpl); }}
                    sx={{ border: "none", background: "transparent", color: muted, opacity: 0.6, fontSize: 15, lineHeight: 1, cursor: "pointer", "&:hover": { opacity: 1 } }}>
                    ×
                  </Box>
                )}
              </Box>
            ))}
          </Box>
          <Button size="small" fullWidth sx={{ mt: 1.2 }} onClick={() => setTplDialogOpen(true)}>
            Save current as template
          </Button>
        </Box>

        {/* timeline */}
        <Box sx={{ position: "relative", bgcolor: surface, border: `1px solid ${border}`, borderRadius: 2, overflow: "hidden", pl: "56px", minHeight: 200 }}>
          {loading && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, bgcolor: `${surface}cc` }}>
              <CircularProgress size={28} />
            </Box>
          )}
          <Box sx={{ position: "relative", height: (DAY_END - DAY_START) * PX }}>
            {hourLines.map((m) => (
              <Box key={m} sx={{ position: "absolute", left: 0, right: 0, top: yOf(m), borderTop: `1px ${m % 60 ? "dashed" : "solid"} ${border}`, opacity: m % 60 ? 0.5 : 1 }}>
                {m % 60 === 0 && (
                  <Box sx={{ position: "absolute", left: -50, top: -8, width: 44, textAlign: "right", fontSize: 11, color: muted, fontVariantNumeric: "tabular-nums" }}>
                    {fmt(m)}
                  </Box>
                )}
              </Box>
            ))}

            {blocks.map((b) => {
              const c = kindColors(b.kind, dark);
              const isDrop = dropId === b.id;
              const isActive = activeId === b.id;
              const twoCol = b.people.length > 4; // wrap crowded blocks (e.g. afternoon activities)
              const short = b.end - b.start <= 30; // ≤30 min → compact inline people
              const canRemovePerson = !b.reflected; // rota-owned incl. residential
              const paidTag =
                b.paid === "no"
                  ? { label: "Unpaid", bg: dark ? "#3a2e1a" : "#f7e6cf", fg: dark ? "#f1c884" : "#8a5a12" }
                  : null;
              const lay = layout[b.id] || { lane: 0, lanes: 1 };
              const N = lay.lanes;
              const gaps = (N - 1) * 4;
              const multi = N > 1;
              return (
                <Box
                  key={b.id}
                  data-block-id={b.id}
                  data-reflected={b.reflected ? "1" : undefined}
                  title={b.notes || undefined}
                  sx={{
                    position: "absolute",
                    left: multi ? `calc(8px + ${lay.lane} * ((100% - 16px - ${gaps}px) / ${N} + 4px))` : 8,
                    width: multi ? `calc((100% - 16px - ${gaps}px) / ${N})` : "auto",
                    right: multi ? "auto" : 8,
                    top: yOf(b.start),
                    minHeight: Math.max((b.end - b.start) * PX, 28), // grow to fit content on short blocks
                    bgcolor: c.bg, color: c.ink,
                    border: `1px ${b.reflected ? "dashed" : "solid"} ${c.bd}`, borderRadius: 1.5,
                    boxShadow: isActive ? "0 8px 26px rgba(20,25,60,.28)" : "0 1px 3px rgba(20,25,60,.10)",
                    outline: isDrop ? `2px dashed ${theme.palette.primary.main}` : "none", outlineOffset: "-2px",
                    overflow: "hidden", display: "flex", flexDirection: "column",
                    zIndex: isActive ? 40 : 1, "&:hover": { zIndex: 30 }, touchAction: "none",
                  }}
                >
                  <Box onPointerDown={(e) => startDrag(e, b, "top")} sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 8, cursor: "ns-resize", zIndex: 5 }} />
                  <Box onPointerDown={(e) => startDrag(e, b, "bottom")} sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 8, cursor: "ns-resize", zIndex: 5 }} />

                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, px: 0.75, pt: 0.6, pb: 0.3 }}>
                    <Box
                      component="span"
                      onPointerDown={(e) => startDrag(e, b, "move")}
                      aria-label="Move block"
                      sx={{ display: "flex", alignItems: "center", cursor: "grab", touchAction: "none", color: "inherit", opacity: 0.4, mt: "1px", "&:hover": { opacity: 0.85 } }}
                    >
                      <DragIndicatorIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 0.6, rowGap: 0.3 }}>
                      {b.reflected ? (
                        <Box component="span" sx={{ fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{b.title}</Box>
                      ) : (
                        <Editable value={b.title} onCommit={(v) => commitTitle(b.id, v)} sx={{ fontWeight: 700, fontSize: 13 }} />
                      )}
                      {b.reflected && (
                        <LockOutlinedIcon sx={{ fontSize: 13, opacity: 0.6 }} titleAccess={b.source === "class" ? "Staff managed in Classes" : b.source === "residential" ? "Auto: on-duty residential staff" : "Staff managed in Activities"} />
                      )}
                      {paidTag && (
                        <Box component="span" sx={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".03em", px: 0.5, py: "1px", borderRadius: 0.6, bgcolor: paidTag.bg, color: paidTag.fg, lineHeight: 1.5, whiteSpace: "nowrap" }}>
                          {paidTag.label}
                        </Box>
                      )}
                      {/* short blocks list their people inline on the title row */}
                      {short && b.people.map((p, i) => (
                        <Box key={i} component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.3, bgcolor: dark ? "rgba(255,255,255,.09)" : "rgba(0,0,0,.06)", borderRadius: 0.6, px: 0.5, fontSize: 11, lineHeight: 1.5 }}>
                          <Box component="span" sx={{ fontWeight: 600, color: isOver(p) ? "error.main" : undefined }} title={hoursTitle(p)}>{p.name}</Box>
                          {p.role ? <Box component="span" sx={{ opacity: 0.7 }}>({p.role})</Box> : null}
                          {canRemovePerson && (
                            <Box component="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => removePerson(b.id, i)}
                              title={b.residential ? "Excuse from residential" : undefined}
                              sx={{ border: "none", background: "transparent", color: "inherit", opacity: 0.5, fontSize: 12, lineHeight: 1, p: 0, cursor: "pointer", "&:hover": { opacity: 1 } }}>
                              ×
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                    <Box component="span" sx={{ fontSize: 11, fontVariantNumeric: "tabular-nums", opacity: 0.85, whiteSpace: "nowrap", mt: "2px" }}>
                      {fmt(b.start)}–{fmt(b.end)}
                    </Box>
                    {!b.reflected && (
                      <Box component="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => removeBlock(b.id)}
                        sx={{ border: "none", background: "transparent", color: "inherit", opacity: 0.5, fontSize: 16, lineHeight: 1, px: 0.3, mt: "-1px", cursor: "pointer", "&:hover": { opacity: 1 } }}>
                        ×
                      </Box>
                    )}
                  </Box>

                  {!short && (
                    <Box sx={{ px: 1, pb: 0.7, overflow: "hidden", ...(twoCol ? { columnCount: 2, columnGap: "10px" } : { display: "flex", flexDirection: "column", gap: "1px" }) }}>
                      {b.people.map((p, i) => (
                        <Box key={i} sx={{ display: "flex", alignItems: "baseline", gap: 0.6, fontSize: 12, lineHeight: 1.35, ...(twoCol ? { breakInside: "avoid" } : {}) }}>
                          <Box component="span" sx={{ fontWeight: 600, whiteSpace: "nowrap", color: isOver(p) ? "error.main" : undefined }} title={hoursTitle(p)}>{p.name}</Box>
                          <Box component="span" sx={{ opacity: p.role ? 0.5 : 0.25 }}>—</Box>
                          {b.reflected ? (
                            <Box component="span" sx={{ opacity: 0.92 }}>{p.role}</Box>
                          ) : (
                            <Editable value={p.role} onCommit={(v) => commitRole(b.id, i, v)} sx={{ opacity: 0.92, minWidth: 8 }} />
                          )}
                          {canRemovePerson && (
                            <Box component="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => removePerson(b.id, i)}
                              title={b.residential ? "Excuse from residential" : undefined}
                              sx={{ ml: "auto", border: "none", background: "transparent", color: "inherit", opacity: 0.45, fontSize: 13, lineHeight: 1, cursor: "pointer", "&:hover": { opacity: 1 } }}>
                              ×
                            </Box>
                          )}
                        </Box>
                      ))}
                      {b.reflected && b.people.length === 0 && (
                        <Box component="span" sx={{ fontSize: 11.5, opacity: 0.6, fontStyle: "italic" }}>None scheduled</Box>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* rail */}
        <Box sx={{ position: { sm: "sticky" }, top: 12, bgcolor: surface, border: `1px solid ${border}`, borderRadius: 2, p: 1.5 }}>
          <Typography sx={{ fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase", color: muted, mb: 0.3 }}>On duty today</Typography>
          <Typography sx={{ fontSize: 11.5, color: muted, mb: 1.5 }}>
            Drag a name onto a block. Greyed = day off. Hours per person; red = over 7h.
            {hoursBaseline ? " Starts from the scheduled total; your edits adjust it." : ""}
            {" Meals & residential don't count, a class counts 3h, and overlapping blocks count once."}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.9 }}>
            {staffPool.length === 0 && !loading && (
              <Typography sx={{ fontSize: 12, color: muted }}>No staff scheduled for this date.</Typography>
            )}
            {staffPool.map((s) => {
              const h = hFor(s);
              const over = h.over;
              const mins = h.effective;
              return (
              <Box
                key={s.id}
                onPointerDown={(e) => startStaffDrag(e, s)}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.1, bgcolor: surface2,
                  border: `1px solid ${over ? theme.palette.error.main : border}`, borderRadius: 1, px: 1.1, py: 0.8,
                  cursor: s.off ? "not-allowed" : "grab", opacity: s.off ? 0.5 : 1,
                  touchAction: "none", userSelect: "none",
                  "&:hover": { borderColor: s.off ? border : theme.palette.primary.main },
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.off ? muted : "primary.main", flex: "none" }} />
                <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.25, minWidth: 0, flex: 1 }}>
                  <Box component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{s.name}</Box>
                  <Box component="span" sx={{ fontSize: 10.5, color: muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.role}</Box>
                </Box>
                {mins > 0 && (
                  <Box component="span" title={hoursTitle(s)}
                    sx={{ fontSize: 11, fontWeight: 700, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: over ? "error.main" : muted, textDecoration: h.clawback ? "underline dotted" : "none" }}>
                    {fmtDur(mins)}
                  </Box>
                )}
              </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Typography sx={{ mt: 2, fontSize: 12, color: muted, lineHeight: 1.6 }}>
        <b>Drag the ⠿ handle</b> to move a block · <b>drag the top/bottom edge</b> to resize (snaps 15 min) · <b>double-click</b> a title or role to rename · <b>drag a name</b> from the rail into a block.
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: 12, color: muted, lineHeight: 1.6 }}>
        Dashed blocks (<LockOutlinedIcon sx={{ fontSize: 12, verticalAlign: "-2px", opacity: 0.7 }} /> activities &amp; classes) are managed in their own modules — you can reposition them here, but their staff is set there.
      </Typography>
      {error && (
        <Typography sx={{ mt: 1, fontSize: 12, color: "error.main" }}>{error}</Typography>
      )}
      </>
      )}

      {ghost && (
        <Box sx={{ position: "fixed", left: ghost.x, top: ghost.y, zIndex: 1400, pointerEvents: "none", transform: "translate(-50%, -140%)", bgcolor: "primary.main", color: "primary.contrastText", fontSize: 12.5, fontWeight: 600, px: 1.4, py: 0.7, borderRadius: 1, boxShadow: "0 8px 22px rgba(0,0,0,.3)" }}>
          {ghost.name}
        </Box>
      )}

      <Dialog open={tplDialogOpen} onClose={() => setTplDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save as template</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: muted, mb: 1.5 }}>
            Saves this day's blocks (titles, kinds, times) as a reusable {campusName} template. Staff names aren&apos;t included — you add those after applying it.
          </Typography>
          <TextField
            autoFocus fullWidth size="small" label="Template name"
            value={tplName}
            onChange={(e) => setTplName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") confirmSaveTemplate(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTplDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmSaveTemplate} disabled={!tplName.trim()}>Save template</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)} sx={{ width: "100%" }}>{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
